import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtMoney, fmtNum } from "@/lib/api";
import { Spinner, Stat, Tag } from "@/components/common";

const RANGES = [["launch", "Since launch (3 Nov)"], ["7d", "Last 7 days"], ["30d", "Last 30 days (whole account)"]];
const STATUS_TONE = { ENABLED: "emerald", PAUSED: "amber", REMOVED: "red" };

const Review = ({ r }) => (
  <div className="flex flex-wrap gap-1">
    {r.approved > 0 && <Tag tone="emerald">{r.approved} approved</Tag>}
    {r.under_review > 0 && <Tag tone="amber">{r.under_review} under review</Tag>}
    {r.disapproved > 0 && <Tag tone="red">{r.disapproved} disapproved</Tag>}
    {!r.approved && !r.under_review && !r.disapproved && <span className="text-slate-500">—</span>}
  </div>
);

const CampaignTable = ({ rows }) => (
  <div className="cc-card mt-4 overflow-x-auto p-4" data-testid="kpi-campaign-table">
    <table className="w-full text-left text-xs">
      <thead className="text-slate-500"><tr>{["Campaign", "Status", "Serving status", "Ad review", "Spend", "Impr.", "Clicks", "Leads", "Cost / lead"].map((h) => <th key={h} className="px-2 py-1.5 font-medium">{h}</th>)}</tr></thead>
      <tbody>{rows.map((c, i) => (
        <tr key={c.campaign} className="border-t border-[#1e293b] text-slate-300" data-testid={`kpi-campaign-row-${i}`}>
          <td className="px-2 py-2 text-slate-100">{c.campaign.replace("TRL | ", "")}</td>
          <td className="px-2 py-2"><Tag tone={STATUS_TONE[c.status] || "muted"}>{c.status}</Tag></td>
          <td className="px-2 py-2" data-testid={`kpi-serving-${i}`}>{c.serving ? <Tag tone={c.serving === "Eligible" ? "emerald" : c.serving.startsWith("Limited") ? "amber" : c.serving === "Paused" ? "muted" : "red"}>{c.serving}</Tag> : <span className="text-slate-500">next sync</span>}</td>
          <td className="px-2 py-2"><Review r={c.review} /></td>
          <td className="px-2 py-2 font-mono">{fmtMoney(c.cost)}</td>
          <td className="px-2 py-2 font-mono">{fmtNum(c.impressions)}</td>
          <td className="px-2 py-2 font-mono">{fmtNum(c.clicks)}</td>
          <td className="px-2 py-2 font-mono" title={`Form ${c.form} · WhatsApp ${c.whatsapp} · Call ${c.call}`}>{c.leads}</td>
          <td className="px-2 py-2 font-mono">{fmtMoney(c.cost_per_lead)}</td>
        </tr>))}</tbody>
    </table>
  </div>
);

const AccountFlags = ({ d }) => {
  const [busy, setBusy] = useState(false);
  const propose = async () => {
    setBusy(true);
    try { await api.post("/proposals/improve-ad-strength"); toast.success("Ad-strength proposal added to Approvals"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const acct = d.account_status && d.account_status !== "ENABLED";
  const bill = d.billing_status && !["APPROVED", null].includes(d.billing_status);
  if (!d.poor_ads && !acct && !bill) return null;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-3 text-xs" data-testid="kpi-flags">
      {acct && <Tag tone="red">Account status: {d.account_status}</Tag>}
      {bill && <Tag tone="red">Billing: {d.billing_status}</Tag>}
      {d.poor_ads > 0 && <><Tag tone="amber" testid="kpi-poor-ads">{d.poor_ads} Back &amp; Spine ad{d.poor_ads === 1 ? "" : "s"} with POOR ad strength</Tag>
        <button data-testid="propose-ad-strength-btn" onClick={propose} disabled={busy} className="cc-btn cc-btn-ghost px-3 py-1 text-xs">Propose: improve ad strength</button></>}
    </div>
  );
};

export const KpiSection = () => {
  const [range, setRange] = useState("launch");
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get("/dashboard/kpis", { params: { range } }).then((r) => setD(r.data)).catch(() => setD({ totals: null, campaigns: [] })); }, [range]);
  const t = d?.totals;
  return (
    <div data-testid="dashboard-kpis">
      <div className="mb-3 flex flex-wrap gap-2" data-testid="kpi-range-selector">
        {RANGES.map(([k, label]) => <button key={k} data-testid={`kpi-range-${k}`} onClick={() => setRange(k)} className={`cc-btn px-3 py-1.5 text-xs ${range === k ? "cc-btn-primary" : "cc-btn-ghost"}`}>{label}</button>)}
      </div>
      {!d ? <Spinner /> : (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Stat label={`${d.scope} · Spend`} value={fmtMoney(t?.cost)} testid="kpi-cost" />
            <Stat label={`${d.scope} · Clicks`} value={fmtNum(t?.clicks)} testid="kpi-clicks" />
            <Stat label={`${d.scope} · Leads`} value={fmtNum(t?.leads)} testid="kpi-leads" />
            <Stat label={`${d.scope} · Cost / lead`} value={fmtMoney(t?.cost_per_lead)} testid="kpi-cpl" />
          </div>
          <div className="mt-2 text-[11px] text-slate-500" data-testid="kpi-note">Google-reported conversions: {fmtNum(t?.conversions)}{t?.cost_per_conversion ? ` (${fmtMoney(t.cost_per_conversion)} each)` : ""}. {d.note}</div>
          {d.campaigns?.length > 0 && <CampaignTable rows={d.campaigns} />}
          <AccountFlags d={d} />
        </>
      )}
    </div>
  );
};
